import axios, {Axios, AxiosError, AxiosResponse} from "axios";
import {types} from "sass";
import Error = types.Error;

export type Nullable<T> = T | null

export interface APIConfig {
    baseUri: string
    tokenCacheKey: string
}


type APIErrorResponseBody = {
    message: string
    validation?: { [key: string]: string }
}

export type APIResponse<Response = {}> =
    {data: Response | APIErrorResponseBody, code: APIErrorCode}

export type APIResult<Response = {}> =
    Omit<APIResponse<Response>, 'data'> & {data: Response} | APIError

export type APIRoute<Request, Response> = {
    contentType: 'application/json' | 'multipart/form-data'
    method: 'GET' | 'POST'
    route: string,
    secure: boolean,
    cacheKey?: string
}

export default abstract class API {
    private token: Nullable<string> = null;
    private loginState: Nullable<'logged_in' | 'not_logged_in' | 'email_verification_needed'> = null

    public constructor(protected readonly config: APIConfig) {
        this.token = this.Get<{token:string}>(config.tokenCacheKey)?.token??null
    }

    protected abstract Get<T>(key: string): Nullable<T>;

    protected abstract Set<T>(key: string, value: T): void;

    protected GetAxios(contentType: 'application/json' | 'multipart/form-data', secure: boolean): Axios {
        if(this.token === null && secure)
            throw new APIError(APIErrorCode.INVALID_CREDENTIALS, 'Нет токена')

        const instance = axios.create({
            baseURL: this.config.baseUri + '/api',
            responseType: 'json',
            headers: {
                'Content-Type': contentType
            }
        })
        if(secure){
            instance.interceptors.request.use(cfg=>{
                cfg.headers!.AuthorizationToken = `Bearer ${this.token}`
                return cfg
            })
        }
        return instance
    }

    public get LoginState(): typeof this.loginState {
        return this.loginState
    }

    public SetLoginState(state: typeof this.loginState){
        this.loginState = state
    }

    protected SetToken(token: string): void {
        this.Set(this.config.tokenCacheKey, {token})
        this.loginState = 'logged_in'
        this.token = token
    }

    public get HasToken(): boolean {
        return !!this.token
    }

    protected async ExecuteRoute<RequestT = null, ResponseT = {}>(
        route: APIRoute<RequestT, ResponseT>,
        request?: RequestT
    ): Promise<APIResult<ResponseT>>{

        try {
            const ax = await this.GetAxios(route.contentType, route.secure)

            let response: APIResponse<ResponseT>

            switch (route.method){
                case "POST": response =
                    (await ax.post<
                        RequestT,
                        AxiosResponse<APIResponse<ResponseT>>,
                        RequestT>
                    (route.route, request)).data;
                    break;
                case "GET": response =
                    (await ax.get<RequestT,
                        AxiosResponse<APIResponse<ResponseT>>,
                        RequestT>(
                        route.route
                    )).data
            }

            if(response.code != APIErrorCode.OK){
                const errorData = (response.data as APIErrorResponseBody)
                return new APIError(response.code,errorData.message, errorData.validation)
            }
            return response as APIResult<ResponseT>
        } catch (error: any){
            if(error instanceof APIError)
                throw error
            return APIError.fromException(error)
        }
    }
}

export enum APIErrorCode {
    UNKNOWN = -1,
    OK = 0,
    VALIDATION_ERROR = 1,
    INVALID_CREDENTIALS = 2,
    PERMISSION_DENIED = 3,
    NOT_ENOUGH_MONEY = 4,
    LEVEL = 5,
    MAX_PRODUCT_AMOUNT = 6
}

export class APIError {
    public constructor(
        private readonly code: APIErrorCode = APIErrorCode.OK,
        private readonly message: string | null = null,
        private readonly validation?: {[key: string]: string}
        ) {}


    public static fromException(error: unknown): APIError {
        if(error instanceof APIError){
            return error
        }
        if(error instanceof AxiosError){
            if(error.response){
                const e = error.response.data as {code: APIErrorCode, message: Nullable<string>}
                return new APIError(e.code, e.message)
            } else {
                return new APIError(APIErrorCode.UNKNOWN)
            }

        } else if (error instanceof Error){
            return new APIError(APIErrorCode.UNKNOWN, error.toString())
        } else {
            return new APIError(APIErrorCode.UNKNOWN)
        }
    }

    public alert(): void {
        alert(this.toString())
    }

    public toString(): string {
        return `Error: ${APIErrorCode[this.code]} (${this.code}). ${this.message&&`Message: ${this.message}`}`
    }

    public get IsOk(): boolean {
        return this.code === APIErrorCode.OK
    }

    public get Message() {
        return this.message
    }

    public get Code() {
        return this.code
    }

    public get Validation(){
        return this.validation
    }
}
